import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const guides = articles.slice(0, 5);

  const logoContent = (
    <>
      <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
        <i className="ri-file-list-3-line text-background-50 text-base md:text-lg"></i>
      </div>
      <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
        AcBuy<span className="text-primary-500">.asia</span>
      </span>
    </>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-background-50/70 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          {isHomePage ? (
            <h1>
              <Link
                to="/"
                className="flex items-center gap-2 group flex-shrink-0"
              >
                {logoContent}
              </Link>
            </h1>
          ) : (
            <Link
              to="/"
              className="flex items-center gap-2 group flex-shrink-0"
            >
              {logoContent}
            </Link>
          )}

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-950 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuidesOpen(false); }}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-950 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[560px] bg-background-50 rounded-lg border border-background-200/70 shadow-sm overflow-hidden z-50">
                  <div className="grid grid-cols-2 gap-0">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-7 h-7 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-right-up-line text-primary-500 text-xs"></i>
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={guidesRef}>
              <button
                onClick={() => { setGuidesOpen(!guidesOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-950 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guides
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guidesOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guidesOpen && (
                <div className="absolute top-full left-0 mt-2 w-[320px] bg-background-50 rounded-lg border border-background-200/70 shadow-sm overflow-hidden z-50">
                  {guides.map((guide) => (
                    <Link
                      key={guide.slug}
                      to={`/guide${guide.slug}`}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      onClick={() => setGuidesOpen(false)}
                    >
                      <div className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-article-line text-accent-500 text-xs"></i>
                      </div>
                      <span className="whitespace-nowrap truncate">{guide.title}</span>
                    </Link>
                  ))}
                  <div className="border-t border-background-200/70 px-4 py-2.5">
                    <Link
                      to="/"
                      className="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
                      onClick={() => setGuidesOpen(false)}
                    >
                      View all guides →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to={`/guide${articles[0].slug}`}
              className="text-sm font-semibold text-foreground-950 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Spreadsheet Guide
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-sm">
          <div className="px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-7 h-7 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-4-line text-primary-500 text-sm"></i>
              </div>
              Home
            </Link>

            <p className="px-3 pt-3 pb-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">
              <i className="ri-grid-line mr-1"></i>14 Categories
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-up-line text-primary-500 text-xs"></i>
                  </div>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            <div className="border-t border-background-200/70 my-2"></div>

            <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">
              <i className="ri-article-line mr-1"></i>Shopping Guides
            </p>
            {guides.map((guide) => (
              <Link
                key={guide.slug}
                to={`/guide${guide.slug}`}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-6 h-6 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-article-line text-accent-500 text-xs"></i>
                </div>
                <span className="truncate">{guide.title}</span>
              </Link>
            ))}

            <div className="border-t border-background-200/70 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-1 px-3 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full text-center transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              Buy It Now
              <i className="ri-arrow-right-line ml-1.5 text-xs"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}